import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Settings, Bell, Search, LogOut, ChevronDown, Home as HomeIcon, LayoutGrid, ClipboardList, BarChart3, HardHat, Code, Palette, Briefcase, TrendingUp, ShieldCheck, Sparkles, AlertTriangle, Code2, Wallet, Layers, Cpu, Store, Workflow, Recycle, Warehouse, User, Building2 } from "lucide-react";
import { usePersona } from "@/lib/personaStore";
import { useAuth } from "@/contexts/AuthContext";
import { useBranding } from "@/lib/branding";
import { useViewMode } from "@/lib/viewMode";
import { endpoints } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import StatusPill from "@/components/StatusPill";
import AskMeDialog from "@/components/AskMeDialog";
import IncidentDialog from "@/components/IncidentDialog";
import { formatDateTime, displayReqNumber } from "@/lib/format";

const ICONS = { Code, Palette, Briefcase, TrendingUp, HardHat };

// User view — everyday self-service
const USER_NAV = [
  { to: "/home", label: "Home", icon: HomeIcon, testId: "nav-home" },
  { to: "/catalogue", label: "Catalog", icon: LayoutGrid, testId: "nav-catalog" },
  { to: "/ai-marketplace", label: "Marketplace", icon: Store, testId: "nav-marketplace" },
  { to: "/aipc", label: "MyPC", icon: Cpu, testId: "nav-mypc" },
  { to: "/requests", label: "My Requests", icon: ClipboardList, testId: "nav-requests" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, testId: "nav-approvals" },
  { to: "/reports", label: "Reports", icon: BarChart3, testId: "nav-reports" },
];

// Admin view — fleet + agents
const ADMIN_NAV = [
  { to: "/admin/cockpit", label: "Cockpit", icon: HomeIcon, testId: "nav-cockpit" },
  { to: "/lifecycle", label: "Lifecycle", icon: Workflow, testId: "nav-lifecycle" },
  { to: "/studio", label: "Studio", icon: Sparkles, testId: "nav-studio" },
  { to: "/recovery", label: "Recovery", icon: Recycle, testId: "nav-recovery" },
  { to: "/admin/warehouse", label: "Warehouse", icon: Warehouse, testId: "nav-warehouse" },
];

// Admin view — secondary pages, tucked under the gear menu
const ADMIN_MORE = [
  { to: "/integrations", label: "Integrations", icon: Code2, testId: "nav-integrations" },
  { to: "/blueprint", label: "Blueprint", icon: Layers, testId: "nav-blueprint" },
  { to: "/finance", label: "Finance", icon: Wallet, testId: "nav-finance" },
];

export default function AppLayout() {
  const { persona, userName, clear } = usePersona();
  const { logout: authLogout } = useAuth();
  const { view, setView } = useViewMode();
  const nav = useNavigate();
  const location = useLocation();
  const Icon = ICONS[persona?.icon] || Briefcase;
  const [notifs, setNotifs] = useState([]);
  const [askOpen, setAskOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [snStatus, setSnStatus] = useState(null);

  const NAV = view === "admin" ? ADMIN_NAV : USER_NAV;

  useEffect(() => {
    endpoints.snStatus().then(setSnStatus).catch(() => setSnStatus({ enabled: false }));
  }, []);

  useEffect(() => {
    endpoints.notifications(persona.id).then(setNotifs).catch(() => {});
    const t = setInterval(() => endpoints.notifications(persona.id).then(setNotifs).catch(() => {}), 20000);
    return () => clearInterval(t);
  }, [persona.id]);

  const logout = () => { clear(); nav("/"); };
  const { brand } = useBranding();
  const initials = (userName || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  const unreadCount = notifs.filter((n) => n.status !== "Fulfilled" && n.status !== "Rejected").length;

  const logoShape =
    brand.logoShape === "chevron"
      ? { clipPath: "polygon(0 0, 100% 0, 100% 60%, 50% 100%, 0 60%)" }
      : { borderRadius: 8 };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="app-layout">
      <div className="stripe-bar h-1.5 w-full" />
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 h-16 flex items-center gap-6">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 grid place-items-center text-white font-black text-lg"
              style={{ ...logoShape, backgroundColor: brand.primary }}
            >
              {brand.logoLetter}
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-base text-slate-900">{brand.wordmark}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold -mt-0.5">{brand.small}</div>
            </div>
          </div>

          <nav className="hidden md:flex items-center gap-1 ml-4" data-testid="primary-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 h-16 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive ? "text-slate-900" : "border-transparent text-slate-600 hover:text-slate-900"
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? { borderColor: brand.primary, color: brand.primary }
                    : {}
                }
              >
                <n.icon size={16} />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1 max-w-md hidden lg:block">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="global-search"
                placeholder="Search or press ? to Ask Me"
                onFocus={() => setAskOpen(true)}
                readOnly
                className="pl-9 h-9 bg-slate-50 border-slate-200 cursor-pointer"
              />
            </div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            {/* View mode toggle */}
            <div className="hidden md:flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="view-mode-toggle">
              <button
                data-testid="view-mode-user"
                onClick={() => { setView("user"); nav("/home"); }}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  view === "user" ? "bg-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
                style={view === "user" ? { color: brand.primary } : {}}
              >
                <User size={12} />
                User
              </button>
              <button
                data-testid="view-mode-admin"
                onClick={() => { setView("admin"); nav("/admin/cockpit"); }}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  view === "admin" ? "bg-white shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
                style={view === "admin" ? { color: brand.primary } : {}}
              >
                <Building2 size={12} />
                Admin
              </button>
            </div>

            {view === "admin" && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="nav-settings-menu"
                    title="Integrations, Blueprint, Finance"
                    className={`w-9 h-9 grid place-items-center rounded-md border transition-colors ${
                      ADMIN_MORE.some((m) => location.pathname.startsWith(m.to))
                        ? "border-slate-300 bg-slate-100"
                        : "border-slate-200 bg-white hover:bg-slate-50"
                    }`}
                    style={ADMIN_MORE.some((m) => location.pathname.startsWith(m.to)) ? { color: brand.primary } : { color: "#475569" }}
                  >
                    <Settings size={16} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-slate-500">More</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {ADMIN_MORE.map((m) => (
                    <DropdownMenuItem key={m.to} data-testid={m.testId} onClick={() => nav(m.to)} className="gap-2 cursor-pointer">
                      <m.icon size={14} />
                      {m.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <a
              href={snStatus?.enabled ? snStatus.instance_url : "https://developer.servicenow.com"}
              target="_blank"
              rel="noreferrer"
              data-testid="sn-status-pill"
              title={snStatus?.enabled ? `Connected to ${snStatus.instance_url}` : "Sign up for a free ServiceNow PDI to enable live integration"}
              className={`hidden md:inline-flex whitespace-nowrap items-center gap-1.5 text-[10px] font-mono px-2 py-1 rounded-md border transition-colors ${
                snStatus?.enabled
                  ? "bg-[#293e40] text-white border-[#293e40] hover:bg-[#1a2828]"
                  : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
              }`}
            >
              <span className={`w-1.5 h-1.5 rounded-full ${snStatus?.enabled ? "bg-[#62d84e] animate-pulse" : "bg-slate-300"}`} />
              {snStatus?.enabled ? "SN · live" : "SN · mock"}
            </a>
            <Button variant="ghost" size="sm" data-testid="ask-me-btn" onClick={() => setAskOpen(true)} className="hidden md:inline-flex">
              <Sparkles size={14} className="mr-1.5 text-[#0033A0]" /> Ask Me
            </Button>
            <Button variant="ghost" size="sm" data-testid="report-issue-btn" onClick={() => setIncidentOpen(true)} className="hidden md:inline-flex text-[#DA291C]">
              <AlertTriangle size={14} className="mr-1.5" /> Report Issue
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="notifications-btn" className="relative">
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DA291C] text-white text-[10px] font-bold grid place-items-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0 bg-white" data-testid="notifications-popover">
                <div className="px-4 py-3 border-b border-slate-200">
                  <div className="font-semibold text-sm text-slate-900">Notifications</div>
                  <div className="text-[11px] text-slate-500">Latest updates on your requests</div>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 && (
                    <div className="p-6 text-center text-xs text-slate-500">No activity yet.</div>
                  )}
                  {notifs.map((n) => {
                    const displayNum = displayReqNumber(n);
                    return (
                    <button
                      key={n.id}
                      data-testid={`notif-${displayNum}`}
                      onClick={() => nav(`/tickets/${n.id}`)}
                      className="w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono text-[11px] font-bold text-[#0033A0]">{displayNum}</span>
                        <StatusPill status={n.status} />
                      </div>
                      <div className="text-sm text-slate-900 mt-1 line-clamp-1">{n.title}</div>
                      <div className="text-[11px] text-slate-500 line-clamp-1">{n.message}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{formatDateTime(n.timestamp)}</div>
                    </button>
                  );})}
                </div>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="persona-menu-btn" className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors">
                  <Avatar className="w-8 h-8">
                    <AvatarFallback className="bg-[#0033A0] text-white text-xs font-bold">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="text-left hidden sm:block">
                    <div className="text-xs font-semibold text-slate-900 leading-none">{userName}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-1">
                      <Icon size={10} /> {persona?.name}
                    </div>
                  </div>
                  <ChevronDown size={14} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-xs">{userName}</div>
                  <div className="text-[11px] text-slate-500 font-normal">{persona?.title}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    const next = view === "admin" ? "user" : "admin";
                    setView(next);
                    nav(next === "admin" ? "/admin/cockpit" : "/home");
                  }}
                  data-testid="menu-switch-view"
                >
                  {view === "admin" ? <User size={14} className="mr-2" /> : <Building2 size={14} className="mr-2" />}
                  Switch to {view === "admin" ? "User" : "Admin"} view
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/home")} data-testid="menu-home">
                  <HomeIcon size={14} className="mr-2" /> Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/requests?view=orders")} data-testid="menu-orders">
                  <ClipboardList size={14} className="mr-2" /> My Orders
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/approvals")} data-testid="menu-approvals">
                  <ShieldCheck size={14} className="mr-2" /> Approvals inbox
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-[#DA291C]">
                  <LogOut size={14} className="mr-2" /> Switch persona
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    clear();
                    await authLogout();
                    nav("/login");
                  }}
                  data-testid="menu-signout"
                  className="text-[#DA291C]"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="md:hidden border-t border-slate-200 overflow-x-auto">
          <div className="flex px-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`m-${n.testId}`}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-3 py-3 text-xs font-medium whitespace-nowrap ${
                    isActive ? "text-[#0033A0] border-b-2 border-[#0033A0]" : "text-slate-600"
                  }`
                }
              >
                <n.icon size={14} /> {n.label}
              </NavLink>
            ))}
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-[1440px] mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-[1440px] mx-auto px-6 py-3 text-xs text-slate-500 flex justify-end">
          <span className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px] bg-white border-slate-200">ServiceNow · BigFix Ready</Badge>
            Flexspace v1.1
          </span>
        </div>
      </footer>

      <AskMeDialog
        open={askOpen}
        onOpenChange={setAskOpen}
        onAction={(action) => {
          if (action?.id === "report_incident") {
            setIncidentOpen(true);
          } else if (action?.id === "view_ticket" && action.params?.req_number) {
            // navigate handled inside dialog; ensure route is visited
            nav("/requests?view=all");
          }
        }}
      />
      <IncidentDialog open={incidentOpen} onClose={() => setIncidentOpen(false)} onSuccess={() => setIncidentOpen(false)} />
    </div>
  );
}
